import { useState } from 'react'
import { Sparkles, RefreshCw, AlertTriangle, CheckCircle, Info } from 'lucide-react'
import { aiApi, type ProjectInsights as ProjectInsightsData } from '../../services/aiApi'

interface ProjectInsightsProps {
  projectId?: string
}

const HEALTH_CONFIG = {
  good: { label: 'Healthy', color: 'var(--accent-green)', icon: CheckCircle },
  warning: { label: 'Needs Attention', color: 'var(--accent-amber)', icon: AlertTriangle },
  critical: { label: 'Critical', color: 'var(--accent-red)', icon: AlertTriangle },
}

const RISK_COLORS: Record<string, string> = {
  high: 'var(--accent-red)',
  medium: 'var(--accent-amber)',
  low: 'var(--accent-blue)',
}

export function ProjectInsights({ projectId }: ProjectInsightsProps) {
  const [insights, setInsights] = useState<ProjectInsightsData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleAnalyze() {
    setLoading(true)
    setError(null)

    try {
      const data = await aiApi.getProjectInsights(projectId || 'all')
      setInsights(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Analysis failed')
    } finally {
      setLoading(false)
    }
  }

  const healthCfg = insights ? HEALTH_CONFIG[insights.health] : null
  const HealthIcon = healthCfg?.icon || Info

  return (
    <div className="card ai-insights-card">
      <div className="card-header">
        <div className="ai-insights-title">
          <Sparkles size={18} className="text-accent" />
          <h3>AI Insights</h3>
        </div>
        <button
          className="btn btn-sm btn-ghost"
          onClick={handleAnalyze}
          disabled={loading}
          title={insights ? 'Refresh analysis' : 'Run analysis'}
        >
          {loading ? (
            <span className="ai-loading">
              <span></span><span></span><span></span>
            </span>
          ) : insights ? (
            <RefreshCw size={16} />
          ) : (
            <>
              <Sparkles size={14} />
              Analyze
            </>
          )}
        </button>
      </div>

      <div className="card-body">
        {!insights && !loading && !error && (
          <div className="ai-insights-empty">
            <Sparkles size={24} className="text-muted" />
            <p>Click Analyze to generate AI-powered insights about your commissioning progress.</p>
          </div>
        )}

        {loading && (
          <div className="ai-insights-loading">
            <div className="ai-pulse"></div>
            <p>Analyzing project data...</p>
          </div>
        )}

        {error && (
          <div className="ai-error">
            <AlertTriangle size={16} />
            <span>{error}</span>
          </div>
        )}

        {insights && !loading && (
          <div className="ai-insights-content">
            <div className="ai-insights-health">
              <HealthIcon size={16} style={{ color: healthCfg?.color }} />
              <span className="ai-insights-health-label" style={{ color: healthCfg?.color }}>
                {healthCfg?.label}
              </span>
            </div>

            <p className="ai-insights-summary">{insights.summary}</p>

            {insights.risks.length > 0 && (
              <div className="ai-insights-section">
                <h4>Risks</h4>
                <div className="ai-insights-risks">
                  {insights.risks.map((risk, i) => (
                    <div key={i} className="ai-insights-risk-item">
                      <span
                        className="ai-insights-risk-dot"
                        style={{ backgroundColor: RISK_COLORS[risk.level.toLowerCase()] || 'var(--text-muted)' }}
                      />
                      <span>{risk.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {insights.recommendations.length > 0 && (
              <div className="ai-insights-section">
                <h4>Recommendations</h4>
                <ul className="ai-insights-list">
                  {insights.recommendations.map((rec, i) => (
                    <li key={i}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            {insights.stalledItems.length > 0 && (
              <div className="ai-insights-section">
                <h4>Stalled Items</h4>
                <ul className="ai-insights-list stalled">
                  {insights.stalledItems.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
